import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import { getLoanSummaryRequest } from 'tio-data/requests/user';

export default class AuthenticatedIdrDashboard0726IndexRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;

  async model() {
    const userId = this.sessionContext.user.id;
    const [loanSummaryResp, personResult, idr0726Result, legacyIdrResult] = await Promise.all([
      userId
        ? this.store.request(getLoanSummaryRequest(userId))
        : Promise.resolve({ content: undefined }),
      this.store.request(
        findRecord<PersonModel>('person', this.sessionContext.user.person.id!, {
          include: 'accounts.financial-institution,accounts.loans',
          reload: true,
        })
      ) as Promise<{ content: { data: PersonModel } }>,
      this.store.request(
        query<RecommendationModel>(
          'recommendation',
          {
            'filter[user]': userId,
            'filter[requesting-app]': 'IDR-0726',
            include: 'user',
            sort: '-created-at',
            'page[offset]': 0,
            'page[limit]': 10,
          },
          // Force a fresh fetch: the questionnaire's didSave returns here right
          // after creating a recommendation, and the default CachePolicy would
          // otherwise serve a stale cached result (missing the new record).
          { reload: true }
        )
      ) as Promise<{ content: { data: RecommendationModel[] } }>,
      // Legacy IDR runs are also surfaced in the history (with a warning), so the
      // user can revisit results from the old Income-Driven Repayment estimator.
      this.store.request(
        query<RecommendationModel>('recommendation', {
          'filter[user]': userId,
          'filter[requesting-app]': 'IDR',
          include: 'user',
          sort: '-created-at',
          'page[offset]': 0,
          'page[limit]': 10,
        })
      ) as Promise<{ content: { data: RecommendationModel[] } }>,
    ]);

    const person = personResult.content.data;
    const idr0726Recommendations = idr0726Result.content.data ?? [];
    const legacyIdrRecommendations = legacyIdrResult.content.data ?? [];

    // async: false relationships — already sideloaded via the include param
    const personAccounts = person.accounts;
    const user = person.user;

    // Combined history: this pilot's runs alongside the legacy IDR runs, newest
    // first. The usage-history table distinguishes and links them appropriately.
    const history = [...idr0726Recommendations, ...legacyIdrRecommendations]
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, 10);

    return {
      accounts: personAccounts,
      loanSummary: loanSummaryResp.content,
      // "Has run the IDR-0726 estimator" is based on IDR-0726 runs only — it
      // gates the Start tile and the Previous Results / What happens next cards.
      mostRecentRecommendation: idr0726Recommendations[0] ?? null,
      history,
      person,
      user,
    };
  }
}
